import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Box, Newspaper, FileText, Wrench, ClipboardCheck, Truck, Users, LogOut, Menu, X,
  Receipt, Activity, Building2, Settings, FileCheck, ShoppingCart, Tag,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { LOGO_DARK_URL } from "../../lib/brand";
import QuotationDraftBar from "../../components/QuotationDraftBar";

// Self-contained PurchaseOrderDraftBar (no external component file required)
const PO_DRAFT_KEY = "uee_po_draft_v1";
const PO_DRAFT_EVENT = "uee-podraft-change";
const PO_DRAFT_RESUME_EVENT = "uee-podraft-resume";
const PO_PATH = "/admin/purchase-orders";

function loadPoDraft() {
  try {
    const raw = localStorage.getItem(PO_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && d.form ? d : null;
  } catch (e) {
    return null;
  }
}

function clearPoDraft() {
  try {
    localStorage.removeItem(PO_DRAFT_KEY);
    window.dispatchEvent(new Event(PO_DRAFT_EVENT));
  } catch (e) {}
}

function PurchaseOrderDraftBar() {
  const [draft, setDraft] = useState(() => loadPoDraft());
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const sync = () => setDraft(loadPoDraft());
    window.addEventListener(PO_DRAFT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PO_DRAFT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!draft) return null;

  const onPoPage = pathname.startsWith(PO_PATH);
  if (onPoPage && draft.open) return null;

  const resume = () => {
    if (onPoPage) {
      window.dispatchEvent(new Event(PO_DRAFT_RESUME_EVENT));
    } else {
      navigate(`${PO_PATH}?resume=1`);
    }
  };

  const discard = () => {
    if (window.confirm("Discard draft? All unsaved data will be lost.")) clearPoDraft();
  };

  const name = (draft.form?.vendor_name || "").trim();
  const title = draft.editingId ? "Editing Purchase Order" : "New Purchase Order (Draft)";

  return (
    <div
      className="fixed bottom-0 right-4 md:right-[330px] z-40 w-[290px] shadow-2xl"
      data-testid="po-draft-bar"
    >
      <div className="flex items-center bg-slate-900 text-white rounded-t-md border border-slate-700 border-b-0">
        <button
          type="button"
          onClick={resume}
          className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left min-w-0 hover:bg-slate-800 rounded-tl-md"
          data-testid="po-draft-resume"
          title="Resume Purchase Order draft"
        >
          <ShoppingCart className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider truncate">{title}</span>
            <span className="block text-[11px] text-slate-400 truncate">
              {name || "Untitled Vendor"} · click to resume
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={discard}
          className="px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-tr-md"
          data-testid="po-draft-discard"
          title="Discard draft"
          aria-label="Discard draft"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* Sidebar nav — staff sees Reports + Expenses only; admin sees everything. */
const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, group: "main" },

  // Field Reports — visible to ALL auth users (admin + staff)
  { to: "/admin/service-reports", label: "Service Reports", icon: Wrench, group: "reports" },
  { to: "/admin/installation-reports", label: "Installation Reports", icon: ClipboardCheck, group: "reports" },
  { to: "/admin/inspection-reports", label: "Safety Equipment Reports", icon: Truck, group: "reports" },
  { to: "/admin/expenses", label: "Expenses", icon: Receipt, group: "reports" },

  // Admin-only sections
  { to: "/admin/quotations", label: "RFQ Inbox", icon: FileText, adminOnly: true, group: "manage" },
  { to: "/admin/estimated-quotations", label: "Estimated Quotations", icon: Receipt, adminOnly: true, group: "manage" },
  { to: "/admin/proforma-invoices", label: "Proforma Invoice", icon: FileCheck, adminOnly: true, group: "manage" },
  { to: "/admin/purchase-orders", label: "Purchase Order", icon: ShoppingCart, adminOnly: true, group: "manage" },
  { to: "/admin/dispatch-stickers", label: "Dispatch Stickers", icon: Tag, adminOnly: true, group: "manage" },
  { to: "/admin/expenses-summary", label: "Expense Summary", icon: Receipt, adminOnly: true, group: "manage" },
  { to: "/admin/sessions", label: "Login Activity", icon: Activity, adminOnly: true, group: "manage" },
  { to: "/admin/products", label: "Products", icon: Box, adminOnly: true, group: "manage" },
  { to: "/admin/news", label: "News", icon: Newspaper, adminOnly: true, group: "manage" },
  { to: "/admin/users", label: "Users", icon: Users, adminOnly: true, group: "manage" },
  { to: "/admin/units", label: "Unit Settings", icon: Settings, adminOnly: true, group: "manage" },
];

export default function AdminLayout() {
  const { user, loading, logout, activeUnit, setActiveUnit } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const items = NAV.filter((n) => !n.adminOnly || user.role === "admin");
  const isAdmin = user.role === "admin";

  // Group rendering helpers
  const reportItems = items.filter((n) => n.group === "reports");
  const manageItems = items.filter((n) => n.group === "manage");
  const mainItem = items.find((n) => n.group === "main");

  return (
    <div className="min-h-screen flex bg-slate-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-slate-950 text-slate-200 transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform duration-200 flex flex-col`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="px-5 py-4 flex items-center gap-3 border-b border-steel-800 bg-black">
          <img src={LOGO_DARK_URL} alt="Universal EE" className="h-12 w-auto" />
        </div>

        {/* Role badge */}
        <div className="px-5 py-3 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Signed in as</div>
              <div className="text-sm font-bold text-white truncate">{user.name}</div>
              <div className="text-xs text-slate-400 truncate font-mono">{user.email}</div>
            </div>
            <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-1 ${
              isAdmin ? "bg-amber-500 text-slate-950" : "bg-slate-700 text-slate-200"
            }`}>{user.role}</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {/* Main */}
          {mainItem && (
            <div>
              {(() => {
                const Icon = mainItem.icon;
                return (
                  <NavLink
                    to={mainItem.to}
                    end={mainItem.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`admin-nav-${mainItem.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors ${
                        isActive
                          ? "bg-amber-500 text-slate-950"
                          : "text-slate-300 hover:bg-slate-900 hover:text-white"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                    {mainItem.label}
                  </NavLink>
                );
              })()}
            </div>
          )}

          {/* Field Reports group — top priority */}
          <div>
            <div className="px-3 mb-2 text-[10px] uppercase tracking-widest font-bold text-amber-400">
              Field Reports
            </div>
            <div className="space-y-1">
              {reportItems.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors ${
                        isActive
                          ? "bg-amber-500 text-slate-950"
                          : "text-slate-300 hover:bg-slate-900 hover:text-white"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" strokeWidth={2} />
                    {n.label}
                  </NavLink>
                );
              })}
            </div>
          </div>

          {/* Admin-only group — Admin Management */}
          {manageItems.length > 0 && (
            <div>
              <div className="px-3 mb-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Admin Management
              </div>
              <div className="space-y-1">
                {manageItems.map((n) => {
                  const Icon = n.icon;
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors ${
                          isActive
                            ? "bg-amber-500 text-slate-950"
                            : "text-slate-300 hover:bg-slate-900 hover:text-white"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4" strokeWidth={2} />
                      {n.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <Button
            data-testid="admin-logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            variant="outline"
            className="w-full rounded-none bg-transparent border-slate-700 text-slate-200 hover:bg-slate-900 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-black/40 z-30 lg:hidden" />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
          <div className="h-16 px-4 md:px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 -ml-2"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="admin-mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="font-heading font-bold text-lg">
                {isAdmin ? "Admin Console" : "Staff Portal"}
              </div>
              <span className="hidden sm:inline-block ml-2 text-[9px] uppercase tracking-widest font-bold text-amber-600 border border-amber-500 px-2 py-0.5">
                Universal EE
              </span>
            </div>
            <a href="/" className="text-xs font-semibold text-slate-600 hover:text-amber-600 hidden md:inline">View Website ↗</a>
          </div>
          {activeUnit && (
            <div className="px-4 md:px-6 pb-2 -mt-1 flex items-center justify-end gap-2 text-[11px]">
              <Building2 className="w-3.5 h-3.5 text-amber-600" />
              <span className="font-mono uppercase tracking-widest text-slate-600">
                Active: <b className="text-slate-900">{activeUnit === "unit1" ? "Unit 01" : "Unit 02"}</b>
              </span>
              <button
                onClick={() => setActiveUnit("")}
                data-testid="switch-unit-btn"
                className="font-semibold text-amber-700 hover:text-amber-600 hover:underline"
              >
                Switch
              </button>
            </div>
          )}
        </header>
        <main className="p-4 md:p-8">
          <Outlet key={activeUnit || "no-unit"} />
        </main>
        {/* Gmail-style minimized draft bars for forms */}
        <QuotationDraftBar />
        <PurchaseOrderDraftBar />
      </div>
    </div>
  );
}